import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Gauge, X } from "lucide-react";

interface NavTiming {
  dns: number;
  tcp: number;
  ttfb: number;
  domInteractive: number;
  domContentLoaded: number;
  loadComplete: number;
  total: number;
}

interface RouteTiming {
  path: string;
  duration: number;
  time: number;
}

interface ResourceTiming {
  name: string;
  duration: number;
  transferSize: number;
  initiatorType: string;
}

const MAX_ROUTES = 8;
const MAX_RESOURCES = 8;

const fmtMs = (ms: number) => `${ms.toFixed(0)}ms`;
const fmtKb = (bytes: number) => (bytes > 0 ? `${(bytes / 1024).toFixed(1)}KB` : "-");

const speedColor = (ms: number) => {
  if (ms >= 1000) return "text-red-400";
  if (ms >= 300) return "text-yellow-400";
  return "text-green-400";
};

const getNavTiming = (): NavTiming | null => {
  const [entry] = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
  if (!entry) return null;
  return {
    dns: entry.domainLookupEnd - entry.domainLookupStart,
    tcp: entry.connectEnd - entry.connectStart,
    ttfb: entry.responseStart - entry.requestStart,
    domInteractive: entry.domInteractive - entry.startTime,
    domContentLoaded: entry.domContentLoadedEventEnd - entry.startTime,
    loadComplete: entry.loadEventEnd - entry.startTime,
    total: entry.duration,
  };
};

function Row({ label, value, ms }: { label: string; value: string; ms?: number }) {
  return (
    <div className="flex items-center justify-between py-0.5 text-xs">
      <span className="text-gray-400">{label}</span>
      <span className={ms !== undefined ? speedColor(ms) : "text-gray-200"}>{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] font-semibold text-gray-300 mb-1 uppercase tracking-wide">
        {title}
      </div>
      {children}
    </div>
  );
}

export default function PerformanceMonitor() {
  const [open, setOpen] = useState(false);
  const [navTiming, setNavTiming] = useState<NavTiming | null>(null);
  const [fcp, setFcp] = useState<number | null>(null);
  const [lcp, setLcp] = useState<number | null>(null);
  const [routes, setRoutes] = useState<RouteTiming[]>([]);
  const [resources, setResources] = useState<ResourceTiming[]>([]);

  const location = useLocation();
  const navStartRef = useRef<number | null>(null);
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    setNavTiming(getNavTiming());

    const observers: PerformanceObserver[] = [];

    try {
      const paintObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === "first-contentful-paint") {
            setFcp(entry.startTime);
          }
        }
      });
      paintObserver.observe({ type: "paint", buffered: true });
      observers.push(paintObserver);
    } catch {
      // paint timing not supported
    }

    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const last = entries[entries.length - 1];
        if (last) setLcp(last.startTime);
      });
      lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
      observers.push(lcpObserver);
    } catch {
      // LCP not supported
    }

    try {
      const resourceObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries() as PerformanceResourceTiming[];
        setResources((prev) => {
          const merged = [
            ...prev,
            ...entries.map((e) => ({
              name: e.name.replace(window.location.origin, ""),
              duration: e.duration,
              transferSize: e.transferSize ?? 0,
              initiatorType: e.initiatorType,
            })),
          ];
          const seen = new Set<string>();
          const deduped = merged.filter((r) => {
            const key = `${r.name}:${r.duration}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          return deduped.sort((a, b) => b.duration - a.duration).slice(0, MAX_RESOURCES);
        });
      });
      resourceObserver.observe({ type: "resource", buffered: true });
      observers.push(resourceObserver);
    } catch {
      // resource timing not supported
    }

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  // SPAページ遷移時間の計測（location変更コミット後、次の描画までの経過時間）
  useEffect(() => {
    const isFirstPath = prevPathRef.current === null;
    if (!isFirstPath && prevPathRef.current !== location.pathname) {
      navStartRef.current = performance.now();
    }
    prevPathRef.current = location.pathname;

    if (navStartRef.current !== null) {
      const start = navStartRef.current;
      navStartRef.current = null;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const duration = performance.now() - start;
          setRoutes((prev) =>
            [{ path: location.pathname, duration, time: Date.now() }, ...prev].slice(0, MAX_ROUTES)
          );
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div className="fixed bottom-1 right-1 z-[9999] font-mono select-none pointer-events-none">
      {open && (
        <div className="pointer-events-auto mb-2 w-80 max-h-[70vh] overflow-y-auto rounded-lg bg-gray-900/95 text-gray-100 shadow-2xl border border-gray-700 p-3 backdrop-blur">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-white">パフォーマンス計測</span>
            <button
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-white"
              aria-label="閉じる"
            >
              <X size={14} />
            </button>
          </div>

          <Section title="初期読み込み (Navigation Timing)">
            {navTiming ? (
              <>
                <Row label="DNS" value={fmtMs(navTiming.dns)} ms={navTiming.dns} />
                <Row label="TCP接続" value={fmtMs(navTiming.tcp)} ms={navTiming.tcp} />
                <Row label="TTFB" value={fmtMs(navTiming.ttfb)} ms={navTiming.ttfb} />
                <Row
                  label="DOM Interactive"
                  value={fmtMs(navTiming.domInteractive)}
                  ms={navTiming.domInteractive}
                />
                <Row
                  label="DOMContentLoaded"
                  value={fmtMs(navTiming.domContentLoaded)}
                  ms={navTiming.domContentLoaded}
                />
                <Row
                  label="Load完了"
                  value={fmtMs(navTiming.loadComplete)}
                  ms={navTiming.loadComplete}
                />
                <Row label="合計" value={fmtMs(navTiming.total)} ms={navTiming.total} />
              </>
            ) : (
              <div className="text-xs text-gray-500">計測データなし</div>
            )}
          </Section>

          <Section title="ペイント">
            <Row label="FCP" value={fcp !== null ? fmtMs(fcp) : "-"} ms={fcp ?? undefined} />
            <Row label="LCP" value={lcp !== null ? fmtMs(lcp) : "-"} ms={lcp ?? undefined} />
          </Section>

          <Section title={`ページ遷移 (直近${MAX_ROUTES}件)`}>
            {routes.length === 0 ? (
              <div className="text-xs text-gray-500">まだ遷移がありません</div>
            ) : (
              routes.map((r) => (
                <Row
                  key={r.time}
                  label={r.path.length > 22 ? `${r.path.slice(0, 22)}…` : r.path}
                  value={fmtMs(r.duration)}
                  ms={r.duration}
                />
              ))
            )}
          </Section>

          <Section title="遅いリソース Top">
            {resources.length === 0 ? (
              <div className="text-xs text-gray-500">計測中...</div>
            ) : (
              resources.map((r, i) => (
                <div key={`${r.name}-${i}`} className="py-0.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className={speedColor(r.duration)}>{fmtMs(r.duration)}</span>
                    <span className="text-gray-500">{fmtKb(r.transferSize)}</span>
                  </div>
                  <div className="text-[10px] text-gray-500 truncate" title={r.name}>
                    [{r.initiatorType}] {r.name}
                  </div>
                </div>
              ))
            )}
          </Section>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        className="pointer-events-auto flex items-center justify-center w-7 h-7 rounded-full bg-gray-900/70 text-white shadow-xl border border-gray-700 opacity-60 hover:opacity-100 hover:bg-gray-800 transition-all ml-auto"
        aria-label="パフォーマンス計測を開く"
        title="パフォーマンス計測"
      >
        <Gauge size={14} />
      </button>
    </div>
  );
}
